import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputLabel,
  Typography,
} from '@mui/material'
import {
  CameraIconGrey,
  ContractDownloadIcon,
  UploadIcon,
} from '../../assets/icons/icons'
import { useRef, useState } from 'react'
import ContractForm from './ContractForm'
import ContractDetails from './ContractDetails'

// @ts-ignore
import * as pdfjsLib from 'pdfjs-dist'

pdfjsLib.GlobalWorkerOptions.workerSrc =
  '//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.3.200/pdf.worker.min.js'

const ContractAI = () => {
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Use States
  const [currentState, setCurrentState] = useState('upload')
  const [isLoading, setIsLoading] = useState(false)

  const [selectedFiles, setSelectedFiles] = useState<any>([])
  const [isBtnLoading, setIsBtnLoading] = useState(false)

  const [title, setTitle] = useState('')
  const [date, setDate] = useState<any>(null)
  const [lastRevision, setLastRevision] = useState(null)
  const [parties, setParties] = useState<string[]>([])
  const [selectedProjects, setSelectedProjects] = useState('')
  const [notes, setNotes] = useState('')

  const [summary, setSummary] = useState('')
  const [pdfText, setPdfText] = useState('')
  const [mutationResponse, setMutationResponse] = useState(null)

  // Handle File upload
  const handleFileUploadPreview = (event: any) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFiles([file])
      const fileReader = new FileReader()

      fileReader.onload = async (e: any) => {
        const typedArray = new Uint8Array(e.target.result as ArrayBuffer)

        try {
          const loadingTask = pdfjsLib.getDocument({ data: typedArray })
          const pdf = await loadingTask?.promise
          let extractedText = ''

          for (let pageNum = 1; pageNum <= pdf?.numPages; pageNum++) {
            const page = await pdf?.getPage(pageNum)
            const textContent = await page?.getTextContent()
            const textItems = textContent?.items
              .map((item: any) => item?.str)
              .join(' ')
            extractedText += textItems + ' '
          }

          setPdfText(extractedText)
        } catch (error) {
          console.error('Error extracting PDF text: ', error)
        }
      }

      fileReader?.readAsArrayBuffer(file)
    }
  }

  // Convert file size to KB
  const formatFileSize = (size: number) => {
    return (size / 1024)?.toFixed(0)
  }

  // Handle Chat AI
  const handleAskQuestions = async () => {
    const questions = [
      {
        question:
          'Please provide the title of the contract. Just give the title without any extra information and extra words. if you dont find just give me empty space and empty space means not the word empty space',
        setter: setTitle,
      },
      {
        question:
          'Please provide the date of the contract in this format MMM DD, YYYY. Just give the date without any extra information and extra words. if you dont find just give me empty space and empty space means not the word empty space',
        setter: setDate,
      },
      {
        question:
          'Please give me the revision date in MMM DD, YYYY format. Dont write anything extra in the response just give me revision date in the asked format If you didnt find then give me empty space and empty space means not the word empty space',
        setter: setLastRevision,
      },
      {
        question:
          'Please provide the names of both parties. Just give me only the names of both without any labels or extra information. if you dont find just give me empty space and empty space means not the word empty space',
        setter: (answer: any) => {
          const fullNames = answer?.split('\n')
          const firstNames = fullNames?.map((name: any) => name?.split(' ')[0])
          setParties(firstNames)
        },
      },
      {
        question:
          'Please provide the project name. Just give the name without any extra information and extra words. Also dont give me in double or single quotes. if you dont find just give me empty space and empty space means not the word empty space',
        setter: setSelectedProjects,
      },
      {
        question:
          'Directly generate an HTML document containing the important requirements of the contract. The document should start with an <html> tag and end with a closing </html> tag. Include relevant headings and paragraphs. Do not include any markdown or annotations such as "```html". Return only the HTML content.',
        setter: setSummary,
      },
    ]

    setIsBtnLoading(true)

    for (const { question, setter } of questions) {
      try {
        const response = await fetch(
          'https://api.openai.com/v1/chat/completions',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer sk-proj-cVlF6FquxZa2OT1eFIi8T3BlbkFJjRViQ8vm9wO39k0iJltd`,
            },
            body: JSON.stringify({
              model: 'gpt-4-turbo',
              messages: [
                { role: 'user', content: `${pdfText}\n\n${question}` },
              ],
            }),
          }
        )

        const data = await response.json()
        if (data?.choices && data?.choices?.length > 0) {
          setter(data?.choices[0]?.message?.content?.trim())
        } else {
          setter('' as any)
        }
      } catch (error) {
        console.error('Error fetching response from ChatGPT', error)

        setter('' as any)
      }
    }

    setIsBtnLoading(false)
    setCurrentState('form')
  }
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files.length > 0) {
      setSelectedFiles([files[0]])
    }
  }

  // Handle Cancel File
  const clearData = () => {
    setSelectedFiles([])
    if (fileInputRef?.current) {
      fileInputRef.current.value = ''
    }

    setTitle('')
    setDate(null)
    setLastRevision(null)
    setSelectedProjects('')
    setParties([])
    setNotes('')
    setPdfText('')
    setSummary('')
    setMutationResponse(null)
  }

  return (
    <>
      <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
        <Box marginBottom={3}>
          <Typography variant='h4'>Contract AI</Typography>
        </Box>

        {currentState === 'upload' ? (
          <Card
            sx={{
              padding: 1,
              boxShadow: 'none',
              border: '1px solid #EEEEEE',
              borderRadius: '10px',
              minHeight: '70vh',
            }}>
            <Box
              sx={{
                paddingBlock: 2,
                paddingInline: 1,
              }}>
              <Typography
                sx={{
                  color: '#5F5C5F',
                  mb: 0.5,
                  fontWeight: 'semibold',
                }}>
                Upload the contract you'd like to check
              </Typography>
              <Box
                sx={{
                  border: '1px dashed #6B646CAC',
                  borderRadius: '8px',
                  padding: '4rem 1rem',
                  textAlign: 'center',
                  height: 294,
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}>
                <Typography
                  paddingLeft={2.5}
                  paddingRight={2.5}
                  fontWeight='500'
                  color='#5F5C5F'>
                  Upload contract. Click select to upload, <br /> drag and drop
                  your files
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '15px',
                  }}>
                  <IconButton
                    sx={{
                      background: '#F0845D',
                      '&:hover': {
                        background: '#F0845D',
                      },
                      borderRadius: 1.8,
                      padding: '6px 10px',
                    }}
                    disabled={isLoading}
                    onClick={() => {
                      let input = document.getElementById('file-upload')
                      input?.click()
                    }}>
                    <UploadIcon />{' '}
                    <p
                      style={{
                        fontSize: 12,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                        margin: 0,
                        color: 'white',
                        paddingLeft: 7,
                      }}>
                      Select File
                    </p>
                  </IconButton>

                  {/* <IconButton
                    sx={{
                      background: '#E6E4EA',
                      '&:hover': {
                        background: '#E6E4EA',
                      },
                      borderRadius: 1.8,
                      padding: '6px 10px',
                      marginLeft: 1,
                    }}
                    disabled={isLoading}
                    onClick={() => setCameraModal(true)}>
                    <CameraIconGrey />{' '}
                    <p
                      style={{
                        fontSize: 12,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                        margin: 0,
                        paddingLeft: 7,
                      }}>
                      Open Camera
                    </p>
                  </IconButton> */}
                </Box>
                <InputLabel htmlFor='file-upload'>
                  <input
                    id='file-upload'
                    type='file'
                    onChange={handleFileUploadPreview}
                    multiple
                    style={{ display: 'none' }}
                    accept='.pdf,.doc,.docx,.xls,.xlsx'
                    ref={fileInputRef}
                  />
                </InputLabel>
              </Box>
              {selectedFiles && selectedFiles?.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                  }}>
                  <>
                    <ContractDownloadIcon />

                    <Typography
                      sx={{
                        color: '#5F5C5F',
                        mr: 1,
                        ml: 1,
                      }}>
                      {selectedFiles[0]?.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#5A525B80',
                      }}>
                      {formatFileSize(selectedFiles[0]?.size) + 'kb'}
                    </Typography>
                  </>
                </Box>
              )}

              {selectedFiles && selectedFiles.length > 0 && (
                <Box
                  sx={{
                    mt: 4,
                  }}>
                  <Button
                    variant='contained'
                    sx={{
                      color: '#24212C',
                      background: '#DCDAE1',
                      fontSize: '13px',
                      textTransform: 'none',
                      py: 1.8,
                      width: '7rem',
                      boxShadow: 'none',
                      ':hover': {
                        background: '#DCDAE1',
                        boxShadow: '1px',
                      },
                      borderRadius: 1.8,
                      mr: 3,
                    }}
                    disabled={isBtnLoading}
                    onClick={clearData}>
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    sx={{
                      color: '#FFF',
                      background: '#F0845D',
                      fontSize: '13px',
                      textTransform: 'none',
                      py: 1.8,
                      width: '17rem',
                      boxShadow: 'none',
                      ':hover': {
                        background: '#F0845D',
                        boxShadow: '1px',
                      },
                      borderRadius: 1.8,
                    }}
                    disabled={isBtnLoading}
                    onClick={handleAskQuestions}>
                    {isBtnLoading ? (
                      <CircularProgress
                        sx={{ color: 'white', width: 20, height: 20 }}
                        size={22}
                      />
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          </Card>
        ) : currentState === 'form' ? (
          <ContractForm
            selectedFiles={selectedFiles}
            setCurrentState={setCurrentState}
            formatFileSize={formatFileSize}
            title={title}
            setTitle={setTitle}
            date={date}
            setDate={setDate}
            lastRevision={lastRevision}
            setLastRevision={setLastRevision}
            parties={parties}
            setParties={setParties}
            selectedProjects={selectedProjects}
            setSelectedProjects={setSelectedProjects}
            notes={notes}
            setNotes={setNotes}
            summary={summary}
            setSummary={setSummary}
            mutationResponse={mutationResponse}
            setMutationResponse={setMutationResponse}
          />
        ) : currentState === 'detail' ? (
          <ContractDetails
            setCurrentState={setCurrentState}
            clearData={clearData}
            mutationResponse={mutationResponse}
            setMutationResponse={setMutationResponse}
            pdfText={pdfText}
          />
        ) : (
          ''
        )}
      </Box>
    </>
  )
}

export default ContractAI
